import { Badge, Button, TabsHeader, TabsHeaderActions, TabsHeaderTitle } from '../ui'
import { FileDown, RefreshCw } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { FilterStatus } from './TransactionsHeader'

export interface SwapsHeaderProps {
  title?: string
  showExportButton?: boolean
  showRefreshButton?: boolean
  selectedStatus?: string | null
  onStatusChange?: (status: FilterStatus | null) => void
  onExportClick?: () => void
  onRefresh?: () => void
  isRefreshing?: boolean
  selectedFlag?: string | null
  onFlagChange?: (flag: string | null) => void
}

const activeClassName = (condition: boolean) => {
  if (condition) {
    return 'border-main text-main-foreground bg-main'
  }
  return 'bg-white text-card-text border-card-border hover:bg-gray-100'
}

const BadgeWrapper = ({
  children,
  onClick,
  isActive,
}: {
  children: React.ReactNode
  onClick: () => void
  isActive: boolean
}) => {
  return (
    <Badge
      variant="outline"
      className={`cursor-pointer px-2.5 py-2 ${activeClassName(isActive)}`}
      onClick={onClick}
    >
      <span className="leading-none">{children}</span>
    </Badge>
  )
}

const getFilterValue = (selectedStatus: string | null, selectedFlag: string | null): string => {
  if (selectedFlag === 'FLAGGED') return 'flagged'
  if (selectedStatus === FilterStatus.Completed) return 'completed'
  if (selectedStatus === FilterStatus.Awaiting) return 'awaiting'
  if (selectedStatus === FilterStatus.Pending) return 'pending'
  return 'all'
}

export function SwapsHeader({
  title = 'Swaps',
  showExportButton = false,
  showRefreshButton = false,
  selectedStatus = null,
  selectedFlag = null,
  onStatusChange,
  onFlagChange,
  onExportClick,
  onRefresh,
  isRefreshing = false,
}: SwapsHeaderProps) {
  return (
    <div className="space-y-4">
      <TabsHeader className="flex-wrap gap-2 md:gap-2 lg:gap-2">
        <div className="flex items-center justify-between md:justify-start flex-row gap-2 md:gap-2 lg:gap-6 flex-wrap">
          <TabsHeaderTitle>{title}</TabsHeaderTitle>

          {onStatusChange && onFlagChange && (
            <div className="flex items-center justify-between">
              {/* Desktop: Show badges */}
              <div className="hidden md:flex items-center gap-2 flex-wrap mb-2 lg:mb-0">
                <BadgeWrapper
                  isActive={!selectedStatus && !selectedFlag}
                  onClick={() => {
                    onStatusChange(null)
                    onFlagChange(null)
                  }}
                >
                  All
                </BadgeWrapper>
                <BadgeWrapper
                  isActive={selectedStatus === FilterStatus.Completed}
                  onClick={() => {
                    onStatusChange(
                      selectedStatus === FilterStatus.Completed ? null : FilterStatus.Completed,
                    )
                    onFlagChange(null)
                  }}
                >
                  Completed
                </BadgeWrapper>

                <BadgeWrapper
                  isActive={selectedStatus === FilterStatus.Awaiting}
                  onClick={() => {
                    onStatusChange(
                      selectedStatus === FilterStatus.Awaiting ? null : FilterStatus.Awaiting,
                    )
                    onFlagChange(null)
                  }}
                >
                  Awaiting
                </BadgeWrapper>
                <BadgeWrapper
                  isActive={selectedStatus === FilterStatus.Pending}
                  onClick={() => {
                    onStatusChange(
                      selectedStatus === FilterStatus.Pending ? null : FilterStatus.Pending,
                    )
                    onFlagChange(null)
                  }}
                >
                  Pending
                </BadgeWrapper>
                <BadgeWrapper
                  isActive={selectedFlag === 'FLAGGED'}
                  onClick={() => {
                    onFlagChange(selectedFlag === 'FLAGGED' ? null : 'FLAGGED')
                    onStatusChange(null)
                  }}
                >
                  Flagged
                </BadgeWrapper>
              </div>

              {/* Mobile: Show dropdown */}
              <div className="md:hidden">
                <Select
                  value={getFilterValue(selectedStatus, selectedFlag)}
                  onValueChange={(value) => {
                    switch (value) {
                      case 'all':
                        onStatusChange(null)
                        onFlagChange(null)
                        break
                      case 'completed':
                        onStatusChange(FilterStatus.Completed)
                        onFlagChange(null)
                        break
                      case 'awaiting':
                        onStatusChange(FilterStatus.Awaiting)
                        onFlagChange(null)
                        break
                      case 'pending':
                        onStatusChange(FilterStatus.Pending)
                        onFlagChange(null)
                        break
                      case 'flagged':
                        onFlagChange('FLAGGED')
                        onStatusChange(null)
                        break
                    }
                  }}
                >
                  <SelectTrigger className="w-[140px] bg-white">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="awaiting">Awaiting</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="flagged">Flagged</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
        <TabsHeaderActions>
          {showRefreshButton && onRefresh && (
            <Button variant="outline" onClick={onRefresh} disabled={isRefreshing}>
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          )}
          {showExportButton && onExportClick && (
            <Button variant="main_action" onClick={() => onExportClick()}>
              <FileDown className="h-4 w-4" />
              Export CSV
            </Button>
          )}
        </TabsHeaderActions>
      </TabsHeader>
    </div>
  )
}
